import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Gauge,
    Users,
    CalendarDays,
    TrendingUp,
    FileText,
    CheckCircle2,
    Car,
    Phone,
    Mail,
    MapPin,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { getLeads, getLeadStats, getLeadStatuses, updateLead } from "@/lib/api";

const STATUS_COLORS = {
    Neu: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    Kontaktiert: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    "Angebot erstellt": "bg-violet-500/15 text-violet-400 border-violet-500/30",
    "Angebot versendet": "bg-violet-500/15 text-violet-400 border-violet-500/30",
    "Termin vereinbart": "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
    "Datei bestellt": "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
    "Datei erhalten": "bg-indigo-500/15 text-indigo-400 border-indigo-500/30",
    "Optimierung durchgeführt": "bg-teal-500/15 text-teal-400 border-teal-500/30",
    Abgeschlossen: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
    Verloren: "bg-red-500/15 text-red-400 border-red-500/30",
};

function Kpi({ icon: Icon, label, value, accent, testid }) {
    return (
        <div data-testid={testid} className="rounded-xl border border-border bg-surface p-5">
            <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">{label}</span>
                <Icon className={`h-4 w-4 ${accent || "text-primary"}`} />
            </div>
            <div className="mt-3 text-3xl font-bold text-white">{value}</div>
        </div>
    );
}

export default function Admin() {
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, l, st] = await Promise.all([getLeadStats(), getLeads(), getLeadStatuses()]);
            setStats(s);
            setLeads(l);
            setStatuses(st);
        } catch (e) {
            toast.error("Daten konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = "Admin CRM · NA Performance PerformanceOS";
        load();
    }, [load]);

    const changeStatus = async (id, status) => {
        try {
            await updateLead(id, { status });
            setLeads((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
            toast.success(`Status geändert: ${status}`);
            getLeadStats().then(setStats);
        } catch (e) {
            toast.error("Statusänderung fehlgeschlagen.");
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0A0A]">
            <header className="sticky top-0 z-30 glass border-b border-white/10">
                <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
                    <Link to="/" className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
                            <Gauge className="h-5 w-5 text-white" />
                        </div>
                        <div className="leading-none">
                            <div className="font-heading text-base font-extrabold text-white">NA PERFORMANCE</div>
                            <div className="text-[9px] tracking-[0.25em] uppercase text-primary font-semibold">
                                Admin CRM
                            </div>
                        </div>
                    </Link>
                    <button
                        data-testid="admin-refresh-btn"
                        onClick={load}
                        className="flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm text-white hover:border-primary/50 transition-colors"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
                <h1 className="font-heading text-2xl font-bold text-white sm:text-3xl">Dashboard</h1>
                <p className="mt-1 text-sm text-[#A1A1AA]">Lead-Management & Verkaufsprozess</p>

                {/* KPIs */}
                <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <Kpi icon={Users} label="Neue Leads" value={stats?.neue_leads ?? "–"} testid="kpi-neue" />
                    <Kpi icon={CalendarDays} label="Heute" value={stats?.heute ?? "–"} accent="text-cyan-400" testid="kpi-heute" />
                    <Kpi icon={TrendingUp} label="Diese Woche" value={stats?.diese_woche ?? "–"} accent="text-violet-400" testid="kpi-woche" />
                    <Kpi icon={FileText} label="Offene Angebote" value={stats?.offene_angebote ?? "–"} accent="text-amber-400" testid="kpi-offene-angebote" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <Kpi icon={CheckCircle2} label="Abgeschlossen" value={stats?.abgeschlossen ?? "–"} accent="text-emerald-400" testid="kpi-abgeschlossen" />
                    <Kpi icon={Users} label="Leads gesamt" value={stats?.total ?? "–"} testid="kpi-leads-gesamt" />
                    <div className="col-span-2 rounded-xl border border-border bg-surface p-5" data-testid="kpi-top-fahrzeuge">
                        <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">Top Fahrzeuge</span>
                        <div className="mt-3 space-y-2">
                            {stats?.top_vehicles?.length ? (
                                stats.top_vehicles.map((v) => (
                                    <div key={v.name} className="flex items-center justify-between text-sm">
                                        <span className="flex items-center gap-2 text-white">
                                            <Car className="h-3.5 w-3.5 text-primary" /> {v.name}
                                        </span>
                                        <span className="text-[#A1A1AA]">{v.count}×</span>
                                    </div>
                                ))
                            ) : (
                                <span className="text-sm text-[#52525B]">Noch keine Daten</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Leads */}
                <div className="mt-10">
                    <h2 className="font-heading text-xl font-bold text-white">Leads &amp; Anfragen</h2>
                    <div className="mt-5 space-y-3">
                        {leads.length === 0 && !loading && (
                            <div className="rounded-xl border border-dashed border-border bg-surface p-10 text-center text-sm text-[#52525B]">
                                Noch keine Anfragen vorhanden.
                            </div>
                        )}
                        {leads.map((lead) => (
                            <div
                                key={lead.id}
                                data-testid={`lead-row-${lead.id}`}
                                className="rounded-xl border border-border bg-surface p-5"
                            >
                                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-base font-semibold text-white">{lead.name}</span>
                                            <span
                                                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                                                    STATUS_COLORS[lead.status] || "border-border text-[#A1A1AA]"
                                                }`}
                                            >
                                                {lead.status}
                                            </span>
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[#A1A1AA]">
                                            {lead.vehicle?.marke && (
                                                <span className="flex items-center gap-1.5">
                                                    <Car className="h-3.5 w-3.5 text-primary" />
                                                    {lead.vehicle.marke} {lead.vehicle.modell}
                                                    {lead.empfohlene_stage ? ` · ${lead.empfohlene_stage}` : ""}
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1.5">
                                                <Phone className="h-3.5 w-3.5" /> {lead.telefon}
                                            </span>
                                            {lead.email && (
                                                <span className="flex items-center gap-1.5">
                                                    <Mail className="h-3.5 w-3.5" /> {lead.email}
                                                </span>
                                            )}
                                            {lead.plz && (
                                                <span className="flex items-center gap-1.5">
                                                    <MapPin className="h-3.5 w-3.5" /> {lead.plz}
                                                </span>
                                            )}
                                        </div>
                                        {lead.nachricht && (
                                            <p className="mt-2 text-sm text-[#71717A]">"{lead.nachricht}"</p>
                                        )}
                                    </div>

                                    <div className="shrink-0">
                                        <Select
                                            value={lead.status}
                                            onValueChange={(v) => changeStatus(lead.id, v)}
                                        >
                                            <SelectTrigger
                                                data-testid={`status-select-${lead.id}`}
                                                className="w-full border-border bg-[#0A0A0A] text-white lg:w-56"
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="border-border bg-surface text-white">
                                                {statuses.map((s) => (
                                                    <SelectItem key={s} value={s} className="focus:bg-surface-elevated">
                                                        {s}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <div className="mt-2 text-right text-xs text-[#52525B]">
                                            {lead.created_at ? new Date(lead.created_at).toLocaleString("de-AT") : ""}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
}
